// src/components/admin/AdminServiceIncidents.jsx
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

const SOURCE_LABELS = {
  vision_ocr: "OCR signatures (Vision)",
  vision_document_ocr: "Analyse de documents (Vision)",
  vision_prediction: "Prédiction de style (Vision)",
  vision_visual_analysis: "Analyse visuelle (Vision)",
  vision_conservation: "Conservation / état (Vision)",
  vision_chat: "Agent conversationnel (Vision)",
  vision_import_catalogue: "Import catalogue (Vision)",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminServiceIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("service_incidents")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (!showResolved) query = query.eq("resolved", false);

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;
      setIncidents(data || []);
    } catch (e) {
      console.error("Chargement incidents:", e);
      setError("Impossible de charger les incidents.");
    } finally {
      setLoading(false);
    }
  }, [showResolved]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id) => {
    setBusyId(id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: uErr } = await supabase
        .from("service_incidents")
        .update({ resolved: true, resolved_at: new Date().toISOString(), resolved_by: user?.id ?? null })
        .eq("id", id);
      if (uErr) throw uErr;
      await load();
    } catch (e) {
      console.error("Résolution incident:", e);
      alert("Échec de la mise à jour de l'incident.");
    } finally {
      setBusyId(null);
    }
  };

  const unresolvedCount = incidents.filter((i) => !i.resolved).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-950">Incidents service</h3>
          <p className="mt-1 text-sm text-gray-500">
            Erreurs techniques captées côté client (ex. clé OpenAI invalide) et masquées à l’utilisateur.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
            Afficher résolus
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
          >
            {loading ? "Actualisation…" : "Actualiser"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
      )}

      {!loading && incidents.length === 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700">
          ✅ Aucun incident {showResolved ? "" : "non résolu"} en ce moment.
        </div>
      )}

      {!showResolved && unresolvedCount > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
          {unresolvedCount} incident{unresolvedCount > 1 ? "s" : ""} non résolu{unresolvedCount > 1 ? "s" : ""}
        </div>
      )}

      <div className="space-y-3">
        {incidents.map((inc) => (
          <div
            key={inc.id}
            className={`rounded-xl border p-4 ${inc.resolved ? "border-gray-200 bg-gray-50" : "border-rose-200 bg-white"}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-gray-900 px-2 py-0.5 text-xs font-semibold text-white">
                    {SOURCE_LABELS[inc.source] || inc.source}
                  </span>
                  {inc.resolved && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">résolu</span>
                  )}
                  <span className="text-xs text-gray-500">{fmtDate(inc.created_at)}</span>
                </div>
                <p className="mt-2 break-words font-mono text-xs text-gray-700">{inc.message || "(sans détail)"}</p>
                {inc.context && Object.keys(inc.context).length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {Object.entries(inc.context)
                      .filter(([, v]) => v != null && v !== "")
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </p>
                )}
              </div>
              {!inc.resolved && (
                <button
                  type="button"
                  onClick={() => resolve(inc.id)}
                  disabled={busyId === inc.id}
                  className="shrink-0 rounded-lg bg-gray-950 px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-60"
                >
                  {busyId === inc.id ? "…" : "Marquer résolu"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
